'use client'

import { useEffect, useState } from 'react'

/**
 * Tracks the browser's network connectivity via `navigator.onLine` and the
 * `online`/`offline` window events.
 *
 * SSR-safe: defaults to `true` on the server and during the first client render
 * (so we never flash an "offline" state before hydration), then syncs to the
 * real value in an effect.
 */
export default function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(true)

  useEffect(() => {
    // Sync immediately in case we mounted while already offline.
    setOnline(navigator.onLine)

    const handleOnline = () => setOnline(true)
    const handleOffline = () => setOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return online
}
